import React, { useCallback } from 'react';
import { Grid } from '~/components/';
import { useForm } from '~/hooks';
import { FaPencilAlt, FaTrash} from 'react-icons/fa';
import EasementItem from './EasementItem';
import { MdAddCircle } from 'react-icons/md';
import { useDeleteSectionDetail, useSaveSectionDetail } from '../../hooks';
import sectionTypes from '../sectionTypes';

/**
 * @typedef LienGridValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId 
 * @property {boolean}  addRecordedInBook
 * @property {[object]} sectionDetails
 * @property {boolean}  addRecordBookFlag
 * 
 * @typedef FormState
 * @property {LienGridValues} values The current form values
 */

/** @type {LienGridValues} */

const defaultState = {
    id: 0,
    sectionDetails: [{
        sectionDetailId: 0,
        type: 'easement',
        lienType: '',
        inFavorOf: '',
        book: '',
        pageNo: '',
        countyRegistry: '',
        otherLienType: ''
    }],    
    addEasementFlag: false,
    easementsDetailData: {},
    editRecord: false,
    titleOpinionId: 0,
    sectionType: sectionTypes.EASEMENTS,
    editMode: true
};

const loadData = async (values) => {
    return {
        ...values,
    };
};

const EasementGrid = (props) => {

    const onLoad = useCallback((values) => {
        return loadData(values);
    }, [props.sectionId]);

    const { state, fieldActions } = useForm({
        initialState: { 
            ...defaultState},
        onLoad,     
        props,       
        debug: true 

    }); 

    const easementDetail =  props.sectionDetails ? props.sectionDetails.map((detail) => {
        return {
            ...detail,
            id: detail.sectionDetailId,
            lienType: detail._data.lienType.value,
            inFavorOf: detail._data.inFavorOf,
            book: detail._data.book,
            pageNo: detail._data.pageNo,
            countyRegistry: detail._data.countyRegistry, 
            otherLienType: detail._data.otherLienType
        } ;
    }) : [];

    const { deleteSectionDetail } = useDeleteSectionDetail(state.values.data, state.values.sectionDetailId);

    const onEditClick = async (data) => {
        await fieldActions.onChange(data, 'easementsDetailData');
        await fieldActions.onChange(true, 'editRecord');
        fieldActions.onChange(true, 'addEasementFlag');
    };

    const onDeleteClick = (id) => {
        deleteSectionDetail(id);
    };

    const addEasementModal = () => {
        fieldActions.onChange(false, 'editRecord');
        fieldActions.onChange(true, 'addEasementFlag');
    };

    const cancelAddEasement = () => {
        fieldActions.onChange(false, 'addEasementFlag');
    }; 

    const { saveField } = useSaveSectionDetail(state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, state.values.sectionDetailId);
    
    const addEasement = async (values) => { 

        const data = {
            sectionId: props.sectionId,
            type: 'easement',
            lienType: values.lienType,
            inFavorOf: values.inFavorOf,
            book: values.book,
            pageNo: values.pageNo,
            countyRegistry: values.countyRegistry, 
            otherLienType: values.otherLienType     
        };
        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, state.values.sectionDetailId);
        await fieldActions.onChange(false, 'addEasementFlag');
        
    };

    const saveEasement = async (newData) => {
        const data = {
            sectionId: props.sectionId,
            sectionDetailId: newData.easementsDetailData.sectionDetailId,
            type: 'easement',
            lienType: newData.lienType,
            inFavorOf: newData.inFavorOf,
            book: newData.book,
            pageNo: newData.pageNo,
            countyRegistry: newData.countyRegistry,
            otherLienType: newData.otherLienType             
        };
        
        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data,state.values.sectionType, props.initialDefaultData, props.titleOpinionId, data.sectionDetailId);
        fieldActions.onChange(false, 'addEasementFlag');
    };

    const columns = [
        {
            title: 'Exception Type',
            field: 'lienType',
            width: 200,
            cell: ({ data }) => data.lienType === 'Other' ? data.otherLienType : data.lienType
        },
        {
            title: 'In Favor Of',
            field: 'inFavorOf',
            width: 200,
            
        },
        {
            title: 'Book',
            field: 'book',
            width: 200,
        },
        {
            title: 'Page No.',
            field: 'pageNo',
            width: 200,
            
        },
        {
            title: 'County Registry',
            field: 'countyRegistry',
            width: 200,
        },
        {
            field: 'edit',
            width: 150,
            canSort: false,
            cell: ({ data }) => {
                return (
                    <>
                        {!props.editMode ? '' :
                            <button className="btn btn-link rightSpace" 
                                onClick={() => onEditClick(data)}>
                                <FaPencilAlt />
                            </button>
                        }
                    </>
                );
            }
        },
        {
            field: 'delete',
            width: 150,
            canSort: false,
            cell: ({ data }) => {
                return (
                    <>
                        {!props.editMode ? '' :                    
                            <button className="btn btn-link rightSpace" 
                                onClick={() => onDeleteClick(data.id)}>
                                <FaTrash />
                            </button>
                        }
                    </>
                );
            }
        }
    ];

    return (
        <React.Fragment>
            <div className="col-span-12 mt-5">
                <Grid
                    className="grid-stretch"
                    columns={columns}
                    data={easementDetail}
                />
                <EasementItem
                    isModalOpen={state.values.addEasementFlag}
                    easementsDetailData={state.values.easementsDetailData}
                    editRecord={state.values.editRecord}
                    onCancel={() => cancelAddEasement()}
                    headerTxt={'Add Easement'}
                    sectionId={props.sectionId}
                    addEasement={(values) => addEasement(values)}
                    saveEasement={(b) => saveEasement(b)}
                />                
            </div>
            {!props.editMode ? '' :
                <div className='mt-auto col-span-12'>
                    <div className="flex justify-start items-center mt-auto">
                        <button
                            className='py-1.5 px-2 rounded-full text-white flex items-center focus:outline-none bg-blue-60 disabled:opacity-50'
                            onClick={() => addEasementModal()}
                        >
                            <MdAddCircle className="mr-2" size="1.25rem" />
                            Add Another
                        </button>
                    </div>
                </div>
            }
        </React.Fragment>
    );
};

export default EasementGrid;